import type { JSXElementConstructor, ReactElement, ReactNode } from 'react';
import React from 'react';
import type { AnyStyledComponent } from 'styled-components';

type ChildCast =
  | React.ReactElement<unknown, JSXElementConstructor<unknown>>
  | AnyStyledComponent;
type Child = { name: string; displayName?: string };

export const getChildrenOfType = (children: ReactNode[], types: Child[]): ReactNode[] => {
  return React.Children.toArray(children).filter(c => {
    const child = c as ChildCast;

    if (!child.type) return true;

    let { name } = child.type;

    if ('displayName' in child.type) {
      name = child.type.displayName;
    }

    return types.some(t => (t.displayName ?? t.name) === name);
  });
};

export const getDeepChildrenOfType = (
  children: ReactNode,
  types: Child[],
): ReactNode[] => {
  const validTypeNames = types.map(t => t.displayName ?? t.name);
  const result: ReactNode[] = [];

  React.Children.forEach(children, c => {
    if (!React.isValidElement(c)) return;

    const child = c as ChildCast;

    let { name } = child.type;
    if ('displayName' in child.type) {
      name = child.type.displayName;
    }

    const isValid = validTypeNames.includes(name);

    let hasValidDescendant = false;
    if (child.props?.children) {
      const nested = getDeepChildrenOfType(child.props.children, types);
      if (nested.length > 0) {
        hasValidDescendant = true;
      }
    }

    if (isValid || hasValidDescendant) {
      result.push(child);
    }
  });

  return result;
};

export const findAllComponentsTypeChildren = (
  children: ReactNode[],
  types: Child[],
): ReactElement[] => {
  return React.Children.toArray(children).filter(c => {
    const child = c as ChildCast;

    if (!child.type) return false;

    let { name } = child.type;

    if ('displayName' in child.type) {
      name = child.type.displayName;
    }

    return types.some(t => (t.displayName ?? t.name) === name);
  }) as ReactElement[];
};

export const findAllTextsTypeFromChildren = (children: ReactNode[]): string[] => {
  return React.Children.toArray(children).filter(c => typeof c === 'string') as string[];
};
